// Copyright 2025, University of Maryland, All Rights Reserved
import React, { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Filter } from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import { Switch } from '@/components/ui';
import { Badge } from '@/components/ui';
import {
  useLLMNodeOverview,
  useLLMOverviewApi,
  llmOverviewKeys,
} from '../../services/llm-overview-api';
import type {
  NodeLLMOverviewItem,
  UpdateNodeLLMRequest,
} from '../../types/llm-node-overview';
import { LLMModelSelectInternal } from './LLMModelSelect';

const ALL = 'all';

// agentId is only unique within a workspace (e.g. "privacy-review" exists in many),
// so an agent's filter identity must be the full org + ws + agentId tuple.
const AGENT_KEY_SEP = '␟';
const agentKey = (it: NodeLLMOverviewItem) =>
  `${it.orgId}${AGENT_KEY_SEP}${it.wsId}${AGENT_KEY_SEP}${it.agentId ?? ''}`;

const matchOrg = (it: NodeLLMOverviewItem, v: string) => v === ALL || it.orgId === v;
const matchApp = (it: NodeLLMOverviewItem, v: string) => v === ALL || (it.appId ?? '') === v;
const matchWs = (it: NodeLLMOverviewItem, v: string) => v === ALL || it.wsId === v;
const matchAgent = (it: NodeLLMOverviewItem, v: string) => v === ALL || agentKey(it) === v;

/** Distinct {value,label} options from rows, keyed by id (skips empty ids). */
const dedupe = (
  rows: NodeLLMOverviewItem[],
  id: (it: NodeLLMOverviewItem) => string | null | undefined,
  name: (it: NodeLLMOverviewItem) => string | null | undefined,
) => {
  const seen = new Map<string, string>();
  rows.forEach((it) => {
    const key = id(it);
    if (key && !seen.has(key)) seen.set(key, name(it) || key);
  });
  return Array.from(seen, ([value, label]) => ({ value, label }));
};

export const LLMNodeOverview: React.FC = () => {
  const { data: items = [], isLoading } = useLLMNodeOverview();
  const api = useLLMOverviewApi();
  const queryClient = useQueryClient();

  const [orgFilter, setOrgFilter] = useState<string>(ALL);
  const [appFilter, setAppFilter] = useState<string>(ALL);
  const [wsFilter, setWsFilter] = useState<string>(ALL);
  const [agentFilter, setAgentFilter] = useState<string>(ALL);

  const updateMutation = useMutation({
    mutationFn: ({
      item,
      data,
    }: {
      item: NodeLLMOverviewItem;
      data: UpdateNodeLLMRequest;
    }) =>
      api.updateNodeLLM(item.orgId, item.wsId, item.pipelineId, item.instanceId, {
        ...data,
        agentId: item.agentId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: llmOverviewKeys.all });
      toast.success('Node LLM updated');
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to update node LLM';
      toast.error('Error', { description: message });
    },
  });

  // Cascading options: each level is narrowed by the levels above it.
  const orgs = useMemo(
    () => dedupe(items, (it) => it.orgId, (it) => it.orgName),
    [items],
  );
  const apps = useMemo(
    () => dedupe(items.filter((it) => matchOrg(it, orgFilter)), (it) => it.appId, (it) => it.appName),
    [items, orgFilter],
  );
  const workspaces = useMemo(
    () => dedupe(
      items.filter((it) => matchOrg(it, orgFilter) && matchApp(it, appFilter)),
      (it) => it.wsId,
      (it) => it.wsName,
    ),
    [items, orgFilter, appFilter],
  );
  // Agent options are keyed by the org+ws+agentId tuple, not agentId alone, so picking
  // one agent filters to exactly that agent. When no single workspace is selected the same
  // agentId can appear in several workspaces, so qualify the label with its workspace.
  const agents = useMemo(() => {
    const rows = items.filter(
      (it) => matchOrg(it, orgFilter) && matchApp(it, appFilter) && matchWs(it, wsFilter),
    );
    const seen = new Map<string, string>();
    rows.forEach((it) => {
      if (!it.agentId) return;
      const value = agentKey(it);
      if (seen.has(value)) return;
      const base = it.agentName || it.agentId;
      seen.set(value, wsFilter === ALL ? `${base} · ${it.wsName || it.wsId}` : base);
    });
    return Array.from(seen, ([value, label]) => ({ value, label }));
  }, [items, orgFilter, appFilter, wsFilter]);

  const filtered = useMemo(
    () =>
      items.filter(
        (it) =>
          matchOrg(it, orgFilter) &&
          matchApp(it, appFilter) &&
          matchWs(it, wsFilter) &&
          matchAgent(it, agentFilter),
      ),
    [items, orgFilter, appFilter, wsFilter, agentFilter],
  );

  const handleOrgChange = (value: string) => {
    setOrgFilter(value);
    setAppFilter(ALL);
    setWsFilter(ALL);
    setAgentFilter(ALL);
  };

  const handleAppChange = (value: string) => {
    setAppFilter(value);
    setWsFilter(ALL);
    setAgentFilter(ALL);
  };

  const handleWsChange = (value: string) => {
    setWsFilter(value);
    setAgentFilter(ALL);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Node LLM Assignments</CardTitle>
        <CardDescription>
          Every analysis node across all workspaces. Change the model or long-context fallback
          inline without navigating to the workspace.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row flex-wrap gap-4 mb-4">
          <Select value={orgFilter} onValueChange={handleOrgChange}>
            <SelectTrigger className="w-[200px]">
              <Filter className="mr-2 h-4 w-4" />
              <SelectValue placeholder="All Organizations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All Organizations</SelectItem>
              {orgs.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={appFilter} onValueChange={handleAppChange}>
            <SelectTrigger className="w-[200px]">
              <Filter className="mr-2 h-4 w-4" />
              <SelectValue placeholder="All Applications" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All Applications</SelectItem>
              {apps.map((a) => (
                <SelectItem key={a.value} value={a.value}>
                  {a.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={wsFilter} onValueChange={handleWsChange}>
            <SelectTrigger className="w-[200px]">
              <Filter className="mr-2 h-4 w-4" />
              <SelectValue placeholder="All Workspaces" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All Workspaces</SelectItem>
              {workspaces.map((w) => (
                <SelectItem key={w.value} value={w.value}>
                  {w.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={agentFilter} onValueChange={setAgentFilter}>
            <SelectTrigger className="w-[200px]">
              <Filter className="mr-2 h-4 w-4" />
              <SelectValue placeholder="All Agents" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All Agents</SelectItem>
              {agents.map((a) => (
                <SelectItem key={a.value} value={a.value}>
                  {a.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="text-center py-8">Loading node LLM assignments...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No analysis nodes found.
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Organization</TableHead>
                  <TableHead className="text-xs">Workspace</TableHead>
                  <TableHead className="text-xs">Agent</TableHead>
                  <TableHead className="text-xs">Node</TableHead>
                  <TableHead className="text-xs">Instance</TableHead>
                  <TableHead className="text-xs w-[260px]">Model</TableHead>
                  <TableHead className="text-xs w-[120px]">Fallback</TableHead>
                  <TableHead className="text-xs w-[260px]">Fallback Model</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((it) => {
                  // agentId must be part of the key: a workspace-owned node and an
                  // agent-owned node can share org/ws/pipeline/instance, and duplicate
                  // React keys leave stale rows in the DOM when filters change.
                  const rowKey = `${agentKey(it)}/${it.pipelineId}/${it.instanceId}`;
                  return (
                    <TableRow key={rowKey}>
                      <TableCell className="text-sm">{it.orgName || it.orgId}</TableCell>
                      <TableCell className="text-sm">{it.wsName || it.wsId}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {it.agentId ? (it.agentName || it.agentId) : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{it.nodeDefinition}</Badge>
                      </TableCell>
                      <TableCell className="text-sm font-mono text-muted-foreground">
                        {it.label || it.instanceId}
                      </TableCell>
                      <TableCell>
                        <LLMModelSelectInternal
                          value={it.modelVariant ?? undefined}
                          onValueChange={(modelVariant) =>
                            updateMutation.mutate({ item: it, data: { modelVariant } })
                          }
                          activeOnly
                          showProviderBadge
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={it.longContextFallbackEnabled}
                          onCheckedChange={(longContextFallbackEnabled) =>
                            updateMutation.mutate({
                              item: it,
                              data: { longContextFallbackEnabled },
                            })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <LLMModelSelectInternal
                          value={it.longContextFallbackModel ?? undefined}
                          onValueChange={(longContextFallbackModel) =>
                            updateMutation.mutate({
                              item: it,
                              data: { longContextFallbackModel },
                            })
                          }
                          activeOnly
                          disabled={!it.longContextFallbackEnabled}
                          showProviderBadge
                          placeholder="No fallback"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
